import type * as React from 'react'

import { cn } from '@/lib/utils'

interface ProgressProps {
  /** Progress value, 0–100. Clamped to that range. */
  value: number
  className?: string
  /** Tailwind classes for the filled bar (e.g. a per-topic accent color). */
  barClassName?: string
  /** Tailwind classes for the track. */
  trackClassName?: string
  /** Show the rounded percentage at the right of the label row. */
  showValue?: boolean
  /** Optional label rendered above the bar. */
  label?: React.ReactNode
  /** Height of the bar. Defaults to `h-2`. */
  size?: 'sm' | 'md' | 'lg'
}

const SIZE_CLASS: Record<NonNullable<ProgressProps['size']>, string> = {
  sm: 'h-1.5',
  md: 'h-2',
  lg: 'h-3',
}

/**
 * A horizontal progress bar. Pure div-based, themed with existing tokens, with
 * an optional label row showing a caption and/or the percentage value.
 */
export function Progress({
  value,
  className,
  barClassName,
  trackClassName,
  showValue = false,
  label,
  size = 'md',
}: ProgressProps) {
  const pct = Math.min(100, Math.max(0, value))

  return (
    <div className={cn('w-full', className)}>
      {(label !== undefined || showValue) && (
        <div className="mb-1 flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground truncate">{label}</span>
          {showValue && (
            <span className="text-foreground shrink-0 font-medium">
              {Math.round(pct)}%
            </span>
          )}
        </div>
      )}
      <div
        className={cn(
          'bg-muted w-full overflow-hidden rounded-full',
          SIZE_CLASS[size],
          trackClassName,
        )}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={cn(
            'bg-primary h-full rounded-full transition-all',
            barClassName,
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}
